/* global localStorage */
(function (global) {
  'use strict';

  // Customer-app side of the Firestore-backed help articles (managed in Plumme Admin).
  //
  // Firestore is the source of truth; a per-user localStorage entry is only a cache so
  // the next session shows the published articles immediately. The bundled articles
  // (help-articles*.jsx + locale-help-articles*) stay the fallback: `getRegistry()`
  // returns null unless the DB holds a complete, valid list for the locale, and
  // `helpArticles()` then uses the bundled registry. The two sources are never mixed.
  //
  // Nothing here throws into the UI: every failure leaves the previous state in place.

  const CACHE_PREFIX = 'plumme-help-articles-v1-';
  const LOADED_EVENT = 'plumme-help-articles-loaded';
  const FRESH_MS = 10 * 60 * 1000; // a successful load is reused this long
  const RETRY_MS = 60 * 1000; // after a failure, wait this long before asking again
  const LOCALES = ['no', 'en'];
  const BLOCK_TYPES = ['heading', 'para', 'step', 'tip', 'figure'];

  // Mirrors HELP_ARTICLE_CATEGORY_LABELS in apps/plummeadmin/shared/contracts/help-articles-db.ts
  // (a root test keeps the two in sync) and the bundled ha.cat.* labels, so category
  // grouping looks the same for DB and bundled articles.
  const CATEGORY_LABELS = {
    start: { no: 'Kom i gang', en: 'Getting started' },
    sales: { no: 'Salg og faktura', en: 'Sales and invoicing' },
    purchases: { no: 'Kjøp og bilag', en: 'Purchases and vouchers' },
    bank: { no: 'Bank og betaling', en: 'Bank and payments' },
    vat: { no: 'MVA og avgift', en: 'VAT and duties' },
    payroll: { no: 'Lønn og ansatte', en: 'Payroll and employees' },
    reports: { no: 'Rapporter', en: 'Reports' },
    closing: { no: 'Årsavslutning', en: 'Year-end closing' },
    assets: { no: 'Eiendeler', en: 'Fixed assets' },
    budget: { no: 'Budsjett', en: 'Budget' },
    personalFinance: { no: 'Privatøkonomi', en: 'Personal finance' },
    setup: { no: 'Oppsett og tilgang', en: 'Setup and access' },
    ai: { no: 'AI', en: 'AI' },
    mcp: { no: 'AI og MCP', en: 'AI and MCP' },
  };

  const isText = (value) => typeof value === 'string';
  const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

  const mapBlock = (raw) => {
    if (!isRecord(raw) || !BLOCK_TYPES.includes(raw.type)) return null;
    switch (raw.type) {
      case 'heading':
        return isText(raw.text) ? { t: 'heading', text: raw.text } : null;
      case 'para':
        return isText(raw.text) ? { t: 'para', text: raw.text, ...(Array.isArray(raw.spans) ? { spans: raw.spans } : {}) } : null;
      case 'step':
        return isText(raw.title) ? { t: 'step', n: Number(raw.n) || 0, title: raw.title, text: isText(raw.text) ? raw.text : '', ...(Array.isArray(raw.spans) ? { spans: raw.spans } : {}) } : null;
      case 'tip':
        return isText(raw.title) ? { t: 'tip', kind: ['info', 'tip', 'warn'].includes(raw.kind) ? raw.kind : 'info', title: raw.title, text: isText(raw.text) ? raw.text : '', ...(Array.isArray(raw.spans) ? { spans: raw.spans } : {}) } : null;
      default: {
        // The public URL wins; without it fall back to the bundled image of the same file name.
        const file = isText(raw.src) ? raw.src.split('/').pop() : '';
        const src = isText(raw.url) && raw.url ? raw.url : (file ? `src/img/help/${file}` : '');
        return src ? { t: 'figure', src, caption: isText(raw.caption) ? raw.caption : '' } : null;
      }
    }
  };

  // DB article (one locale, as returned by getHelpArticlesHttp) -> the in-app shape.
  // Returns null for an article that cannot be shown.
  // `why` (optional) receives a short non-PII cause when the article is rejected.
  const mapArticle = (raw, locale, why) => {
    const reject = (reason) => { if (why) why.reason = reason; return null; };
    if (!isRecord(raw) || !isText(raw.id) || !raw.id) return reject('missing id');
    if (!isText(raw.title) || !raw.title.trim()) return reject('blank title');
    const blocks = (Array.isArray(raw.blocks) ? raw.blocks : []).map(mapBlock).filter(Boolean);
    if (!blocks.length) return reject('no valid blocks');
    // A step without a usable number takes its 1-based position among the steps.
    let stepPosition = 0;
    for (const block of blocks) {
      if (block.t !== 'step') continue;
      stepPosition += 1;
      if (!(block.n > 0)) block.n = stepPosition;
    }
    const labels = CATEGORY_LABELS[raw.category];
    return {
      id: raw.id,
      title: raw.title,
      category: labels ? labels[locale] || labels.en : String(raw.category || ''),
      categoryKey: String(raw.category || ''),
      read: isText(raw.read) ? raw.read : '',
      route: isText(raw.route) && raw.route ? raw.route : null,
      keywords: Array.isArray(raw.keywords) ? raw.keywords.filter(isText).join(' ') : '',
      lead: isText(raw.lead) ? raw.lead : '',
      blocks,
      source: 'db',
    };
  };

  // Whole-list mapping: ordered by DB `order`, keyed by id. An empty or partly invalid
  // list yields null, so the caller falls back to the bundled articles instead of mixing.
  // `onReject(reason)` (optional) is told why a non-empty list was thrown away.
  const mapRegistry = (articles, locale, onReject) => {
    if (!Array.isArray(articles) || !articles.length) return null;
    const ordered = articles.slice().sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0) || String(a?.id).localeCompare(String(b?.id)));
    const registry = {};
    for (const raw of ordered) {
      const why = {};
      const article = mapArticle(raw, locale, why);
      const id = isRecord(raw) && isText(raw.id) ? raw.id : '?';
      if (!article) { onReject?.(`article ${id}: ${why.reason || 'invalid'}`); return null; }
      if (registry[article.id]) { onReject?.(`article ${id}: duplicate id`); return null; }
      registry[article.id] = article;
    }
    return registry;
  };

  const createHelpArticleStore = ({ fetchRemote, getUserId, getLocale, storage, emit, now = () => Date.now() } = {}) => {
    const memory = new Map(); // `${uid}:${locale}` -> { version, articles, registry }
    const attempts = new Map(); // key -> { at, ok }
    const inflight = new Map();
    const settledKeys = new Set(); // a load for this user/locale has finished (ok or not)
    const pendingChange = new Set(); // version changed during the running load
    const warned = new Set(); // `${key}:${version}` already reported
    const warnRejected = (key, version) => (reason) => {
      const id = `${key}:${version}`;
      if (warned.has(id)) return;
      warned.add(id);
      try { console.warn(`[help-articles] Published list ignored, using bundled articles (${reason})`); } catch (error) { /* no console */ }
    };

    const keyFor = (uid, locale) => `${uid}:${locale}`;
    const cacheKey = (uid, locale) => `${CACHE_PREFIX}${uid}-${locale}`;
    const currentLocale = () => {
      const locale = String(getLocale?.() || 'no');
      return LOCALES.includes(locale) ? locale : null;
    };
    const currentUser = () => {
      try { return String(getUserId?.() || ''); } catch (error) { return ''; }
    };

    const readCache = (uid, locale) => {
      try {
        const parsed = JSON.parse(storage?.getItem(cacheKey(uid, locale)) || 'null');
        if (!isRecord(parsed) || !isText(parsed.version) || !Array.isArray(parsed.articles)) return null;
        return { version: parsed.version, articles: parsed.articles, registry: mapRegistry(parsed.articles, locale, warnRejected(keyFor(uid, locale), parsed.version)) };
      } catch (error) {
        return null;
      }
    };
    const writeCache = (uid, locale, entry) => {
      try { storage?.setItem(cacheKey(uid, locale), JSON.stringify({ version: entry.version, articles: entry.articles, savedAt: now() })); } catch (error) { /* cache only */ }
    };

    const entryFor = (uid, locale) => {
      const key = keyFor(uid, locale);
      if (!memory.has(key)) {
        const cached = readCache(uid, locale);
        if (cached) memory.set(key, cached);
      }
      return memory.get(key) || null;
    };

    // Synchronous: the in-app registry for the active user and locale, or null (use bundled).
    const getRegistry = () => {
      const uid = currentUser();
      const locale = currentLocale();
      if (!uid || !locale) return null;
      return entryFor(uid, locale)?.registry || null;
    };

    const run = async (uid, locale) => {
      const previous = entryFor(uid, locale);
      const response = await fetchRemote({ locale, ...(previous ? { ifVersion: previous.version } : {}) });
      if (!isRecord(response) || response.ok !== true || !isText(response.version)) return false;
      if (response.notModified) return Boolean(previous && previous.version === response.version);
      if (!Array.isArray(response.articles)) return false;
      // A list the mapper rejects is kept (so ifVersion still short-circuits) but yields no registry.
      const entry = { version: response.version, articles: response.articles, registry: mapRegistry(response.articles, locale, warnRejected(keyFor(uid, locale), response.version)) };
      memory.set(keyFor(uid, locale), entry);
      writeCache(uid, locale, entry);
      if (!previous || previous.version !== entry.version) pendingChange.add(keyFor(uid, locale));
      return true;
    };

    // Loads (or revalidates) the articles for the active user/locale. Resolves true when the
    // store holds a response for the current version, false otherwise. Never rejects.
    const load = ({ force = false } = {}) => {
      const uid = currentUser();
      const locale = currentLocale();
      if (!uid || !locale || typeof fetchRemote !== 'function') return Promise.resolve(false);
      const key = keyFor(uid, locale);
      if (inflight.has(key)) return inflight.get(key);
      const last = attempts.get(key);
      if (!force && last && now() - last.at < (last.ok ? FRESH_MS : RETRY_MS)) return Promise.resolve(last.ok);
      const promise = run(uid, locale)
        .catch(() => false)
        .then((ok) => {
          attempts.set(key, { at: now(), ok });
          inflight.delete(key);
          // Emit when the data changed AND the first time this user/locale settles: a
          // component that rendered the bundled registry earlier (no user yet, or a
          // notModified cache hit) must re-read even though the version did not change.
          const first = !settledKeys.has(key);
          settledKeys.add(key);
          const changed = pendingChange.delete(key);
          if (first || changed) emit?.({ locale, version: entryFor(uid, locale)?.version || '', settled: true });
          return ok;
        });
      inflight.set(key, promise);
      return promise;
    };

    // True once the DB answer for the active user/locale is final for this session (loaded,
    // failed or empty), so "unknown id" can safely mean "not an article". Without a store
    // endpoint nothing will ever load, so that counts as settled; without a user it does not.
    const isSettled = () => {
      if (typeof fetchRemote !== 'function') return true;
      const uid = currentUser();
      const locale = currentLocale();
      return Boolean(uid && locale && settledKeys.has(keyFor(uid, locale)));
    };

    return { load, getRegistry, isSettled, mapArticle, mapRegistry };
  };

  const browser = typeof global.addEventListener === 'function' && typeof global.document !== 'undefined';
  const safeStorage = () => { try { return global.localStorage; } catch (error) { return null; } };

  const store = createHelpArticleStore({
    fetchRemote: (request) => {
      const fetcher = global.PlummeBackend?.fetchHelpArticles;
      return typeof fetcher === 'function' ? fetcher(request) : Promise.resolve(null);
    },
    getUserId: () => global.PlummeBackend?.getHelpArticlesUserId?.() || '',
    getLocale: () => global.PlummeI18n?.getLocale?.() || 'no',
    storage: browser ? safeStorage() : null,
    emit: (detail) => {
      try { global.dispatchEvent(new CustomEvent(LOADED_EVENT, { detail })); } catch (error) { /* no DOM */ }
    },
  });

  // Activate on first help use. Auth and locale changes refresh only an activated store.
  // Repeated events are absorbed by the freshness window.
  let scheduled = false, requested = false;
  const schedule = () => {
    if (!requested || scheduled) return;
    scheduled = true;
    const start = () => { scheduled = false; void store.load(); };
    if (typeof global.requestIdleCallback === 'function') global.requestIdleCallback(start, { timeout: 4000 });
    else global.setTimeout(start, 1500);
  };

  if (browser) {
    global.addEventListener('plumme-backend-state', schedule);
    global.addEventListener('plumme-locale-changed', schedule);
  }

  global.PlummeHelpStore = {
    ...store,
    // Called from helpArticles(); cheap when nothing is due.
    ensure: () => { requested = true; if (browser) schedule(); },
    LOADED_EVENT,
    CATEGORY_LABELS,
    createHelpArticleStore,
  };
})(typeof window !== 'undefined' ? window : globalThis);
